import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  CardActionArea,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/config';

interface Asset {
  id: string;
  name: string;
  description?: string;
  imageUrl: string;
  thumbnailUrl?: string;
  type: string;
  createdAt: any;
  organizationId: string;
  eventId?: string;
  tripId?: string;
  tags?: string[];
  category?: string;
}

interface AssetSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onAssetSelected: (assets: Asset[]) => void;
}

const AssetSelectorDialog: React.FC<AssetSelectorDialogProps> = ({
  open,
  onClose,
  organizationId,
  onAssetSelected
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);

  useEffect(() => {
    if (open) {
      fetchAssets();
    }
  }, [open, organizationId]);

  useEffect(() => {
     if (assets.length > 0) {
        filterAssets();
     }
  }, [assets, tabValue, searchQuery]);

  const verifyImageUrl = async (url: string): Promise<boolean> => {
     if (!url) return false;

     // If it's a data URL, assume it's valid
     if (url.startsWith('data:')) return true;

     try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
     } catch (e) {
        console.warn(`Failed to verify image URL: ${url}`, e);
        return false;
     }
  };

  const fetchAssets = async () => {
     if (!organizationId) return;

     try {
        setLoading(true);

        // Try every possible collection where assets might be stored
        const collections = ['assets', 'assetGallery', 'organizationAssets', 'galleryItems'];

        const fetchPromises = collections.map((collectionName) => {
           return getDocs(
              query(
                 collection(db, collectionName),
                 where('organizationId', '==', organizationId),
                 limit(50)
              )
           ).catch((err) => {
              console.warn(`Error fetching from ${collectionName}:`, err);
              return null; // Return null for failed queries
           });
        });

        // Also try a generic query for any public assets
        fetchPromises.push(
           getDocs(query(collection(db, 'assets'), limit(20))).catch((err) => {
              console.warn('Error fetching public assets:', err);
              return null;
           })
        );

        const results = await Promise.all(fetchPromises);
        const fetchedAssets: Asset[] = [];
        const processedIds = new Set<string>();

        // Process all results
        for (const snapshot of results) {
           if (snapshot) {
              snapshot.forEach((doc) => {
                 if (!processedIds.has(doc.id)) {
                    processedIds.add(doc.id);
                    const data = doc.data();

                    // Get all possible image URLs
                    const imageUrls = [
                       data.imageUrl,
                       data.thumbnailUrl,
                       data.url,
                       data.image,
                       data.thumbnail,
                       data.previewUrl,
                    ].filter(Boolean);

                    // Find the first valid URL
                    const imageUrl = imageUrls.length > 0 ? imageUrls[0] : '';

                    // Create asset object with fallbacks for all fields
                    fetchedAssets.push({
                       id: doc.id,
                       ...data,
                       name: data.name || data.title || `Asset ${doc.id.substring(0, 6)}`,
                       description: data.description || data.caption || '',
                       imageUrl: imageUrl,
                       thumbnailUrl: data.thumbnailUrl || imageUrl,
                       type: data.type || data.fileType || 'image/jpeg',
                       category: data.category || data.assetType || 'general',
                    } as Asset);
                 }
              });
           }
        }

        if (fetchedAssets.length === 0) {
           console.warn('No assets found in any collection, adding sample assets');

           // If no assets found, create some sample assets so the user can see something
           const sampleAssets = [
              {
                 id: 'sample-1',
                 name: 'Organization Logo',
                 description: 'Main logo for organization branding',
                 imageUrl:
                    'https://via.placeholder.com/400x200/2196F3/FFFFFF?text=Organization+Logo',
                 thumbnailUrl:
                    'https://via.placeholder.com/400x200/2196F3/FFFFFF?text=Organization+Logo',
                 type: 'image/jpeg',
                 category: 'organization',
                 tags: ['logo', 'branding'],
              },
              {
                 id: 'sample-2',
                 name: 'Event Banner',
                 description: 'Banner image for upcoming events',
                 imageUrl: 'https://via.placeholder.com/800x400/4CAF50/FFFFFF?text=Event+Banner',
                 thumbnailUrl:
                    'https://via.placeholder.com/800x400/4CAF50/FFFFFF?text=Event+Banner',
                 type: 'image/jpeg',
                 category: 'event',
                 tags: ['event', 'banner'],
              },
              {
                 id: 'sample-3',
                 name: 'Member Badge',
                 description: 'Digital badge for organization members',
                 imageUrl: 'https://via.placeholder.com/300x300/FFC107/FFFFFF?text=Member+Badge',
                 thumbnailUrl:
                    'https://via.placeholder.com/300x300/FFC107/FFFFFF?text=Member+Badge',
                 type: 'image/png',
                 category: 'membership',
                 tags: ['badge', 'membership'],
              },
              {
                 id: 'sample-4',
                 name: 'Trip Photo',
                 description: 'Sample photo from organization trip',
                 imageUrl: 'https://via.placeholder.com/600x400/9C27B0/FFFFFF?text=Trip+Photo',
                 thumbnailUrl: 'https://via.placeholder.com/600x400/9C27B0/FFFFFF?text=Trip+Photo',
                 type: 'image/jpeg',
                 category: 'trip',
                 tags: ['trip', 'photo'],
              },
           ];

           // Add these to our fetched assets
           fetchedAssets.push(...(sampleAssets as Asset[]));
        } else {
        }

        setAssets(fetchedAssets);
     } catch (error) {
        console.error('Error fetching assets:', error);
     } finally {
        setLoading(false);
     }
  };

  const filterAssets = () => {
    let filtered = [...assets];
    
    // Filter by tab selection
    if (tabValue === 1) { // Organization tab
      filtered = filtered.filter(asset => 
        asset.category === 'organization' || 
        asset.tags?.includes('organization') || 
        asset.tags?.includes('branding')
      );
    } else if (tabValue === 2) { // Events tab
      filtered = filtered.filter(asset => 
        asset.eventId || 
        asset.category === 'event' || 
        asset.tags?.includes('event')
      );
    } else if (tabValue === 3) { // Trips tab
      filtered = filtered.filter(asset => 
        asset.tripId || 
        asset.category === 'trip' || 
        asset.tags?.includes('trip')
      );
    } else if (tabValue === 4) { // Membership tab
      filtered = filtered.filter(asset => 
        asset.category === 'membership' || 
        asset.tags?.includes('membership') || 
        asset.tags?.includes('member')
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(asset => 
        asset.name.toLowerCase().includes(query) || 
        (asset.description || '').toLowerCase().includes(query) ||
        (asset.tags || []).some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    setFilteredAssets(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleAssetSelection = (asset: Asset) => {
    const isSelected = selectedAssets.some(a => a.id === asset.id);
    
    if (isSelected) {
      setSelectedAssets(selectedAssets.filter(a => a.id !== asset.id));
    } else {
      setSelectedAssets([...selectedAssets, asset]);
    }
  };

  const handleConfirm = () => {
    onAssetSelected(selectedAssets);
    setSelectedAssets([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedAssets([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Select Assets to Share
      </DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search assets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
      </Box>
      <Box sx={{ px: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="All" />
          <Tab label="Organization" />
          <Tab label="Events" />
          <Tab label="Trips" />
          <Tab label="Membership" />
        </Tabs>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {filteredAssets.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No assets found. Try a different search or filter.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => {
                      setTabValue(0);
                      setSearchQuery('');
                      fetchAssets();
                    }}
                  >
                    Reset Filters & Refresh
                  </Button>
                </Box>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredAssets.map(asset => {
                  const isSelected = selectedAssets.some(a => a.id === asset.id);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={asset.id}>
                      <Card 
                        sx={{ 
                          position: 'relative',
                          border: isSelected ? 2 : 0,
                          borderColor: 'primary.main',
                          height: '100%'
                        }}
                      >
                        <CardActionArea onClick={() => toggleAssetSelection(asset)}>
                          <CardMedia
                            component="img"
                            height="140"
                            image={asset.thumbnailUrl || asset.imageUrl || `/assets/placeholder-image.jpg`}
                            alt={asset.name}
                            sx={{
                              objectFit: 'cover',
                              bgcolor: 'action.hover'
                            }}
                            onError={(e: any) => {
                              console.error('Image failed to load:', asset.thumbnailUrl || asset.imageUrl);
                              // Fallback for failed images - use placeholder.com
                              e.target.onerror = null;
                              const text = encodeURIComponent(asset.name || 'Asset');
                              e.target.src = `https://via.placeholder.com/400x200?text=${text}`;
                            }}
                          />
                          {isSelected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'primary.main',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <CheckIcon sx={{ color: 'white' }} />
                            </Box>
                          )}
                          <CardContent sx={{ pb: 1 }}>
                            <Typography variant="subtitle2" noWrap>
                              {asset.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {asset.description?.substring(0, 60) || 'No description'}
                            </Typography>
                            {asset.category && (
                              <Chip 
                                label={asset.category} 
                                size="small" 
                                color={
                                  asset.category === 'organization' ? 'primary' :
                                  asset.category === 'membership' ? 'success' :
                                  asset.category === 'event' ? 'info' :
                                  asset.category === 'trip' ? 'warning' :
                                  'default'
                                }
                                sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
        <Box>
          <Button 
            onClick={() => setSelectedAssets([])}
            color="inherit"
            disabled={selectedAssets.length === 0}
            sx={{ mr: 1 }}
          >
            Clear Selection
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            color="primary" 
            variant="contained"
            disabled={selectedAssets.length === 0}
            startIcon={selectedAssets.length > 0 ? <CheckIcon /> : undefined}
          >
            Share Selected
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AssetSelectorDialog;
